import { Collapse, CollapseProps, Empty, Spin } from "antd";
import { FC, useState } from "react";
import Icon from "../../assets/icons/icon";
import Details from "../../components/detailsList";
import UserStore from "../../contexts/userStore";
import _t from "../../lang/translate";
import { BaseVehicle, Vehicle } from "../../types/appTypes";
import format from "../../utilities/formatNumbers";
import { printReadableECode } from "../../utilities/stringExtraFunctions";
import { isString, isValidNum } from "../../utilities/typeGuard";

const { Panel } = Collapse;

function isVehicle(vehicle: Vehicle | BaseVehicle): vehicle is Vehicle {
  return 'remoteData' in vehicle && !!vehicle.remoteData;
}
const CAR_API_ORIGIN = process.env.REACT_APP_CAR_API_ORIGIN

type Props = {
  vehicle: Vehicle | null;
  showAdditionalData?: boolean;
  showDMRAlerts?: boolean;
  motorAtlasIdCallback?: () => void;
  loadingMotorAtlasCatalog?: boolean;
};

const VehicleModelData: FC<Props> = ({ vehicle, showAdditionalData, motorAtlasIdCallback, loadingMotorAtlasCatalog }) => {
  const { hasPermission } = UserStore.useContainer();
  const [pinned, setPinned] = useState(false);
  const [hover, setHover] = useState(false);

  const isOpen = pinned || hover;
  const activeKey: CollapseProps["activeKey"] = isOpen ? ["1"] : [];

  if (!vehicle) {
    return <Empty />;
  }
  const {
    motorAtlasId,
    brand,
    model,
    modelDesignation,
    modelPeriod,
    equipmentVariant,
    fuel,
    engineSize,
    horsePower,
    gearbox,
    body,
    driveType,
    modelPeriodRange,
  } = vehicle;

  const formatValue = (v?: unknown | null) => {
    if (isString(v)) {
      return v.trim()
    } else if (isValidNum(v) || typeof v === 'boolean') {
      return v + "";
    } else {
      return "-";
    }
  };

  return (
    <div style={{ background: "#F8F9FB", padding: 16, borderRadius: 4 }}>
      <Details className="mb-0">
        <Details.Title bold className="mt-0" extra={
          <span className="tag-btn cursor-pointer" onClick={motorAtlasIdCallback}>
            {motorAtlasId ? printReadableECode(motorAtlasId) : "Åbn modelkatalog"}
            {loadingMotorAtlasCatalog && (
              <Spin className="tiny-spinner" size="small" spinning />
            )}
          </span>
        }>Modeldata</Details.Title>
        <Details.Item label={_t("brand")} value={formatValue(brand)} />
        <Details.Item
          label={_t("model", "description")}
          value={formatValue(model)}
        />
        <Details.Item
          label={_t("variant")}
          value={formatValue(modelPeriod)}
        />
        <Details.Item label={_t("trim")} value={formatValue(body)} />
        <Details.Item
          label={_t("engine_power")}
          value={formatValue(horsePower)}
        />
        {isVehicle(vehicle) && (
          <Details.Item label={_t("co2_emission")} value={format.emission(vehicle.remoteData?.dat?.mapped?.emissionAmount)} />
        )}

        {isVehicle(vehicle) && showAdditionalData && hasPermission("tax.isAdmin") && (
          <Collapse expandIconPosition="end" bordered={false} ghost
            activeKey={activeKey} onChange={() => setPinned((v) => !v)}
            className="mt-05 mb-0 vehicle-card-collapse">
            <Panel header={pinned && <Icon size="medium" name="pin-outline" />} key="1" style={{ padding: 0 }}
              extra={<div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                style={{ position: "absolute", width: "40%", height: "calc(100% + 16px)", right: 0, top: 0 }}
              />}
            >
              <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} >
                <Details className="mb-0-important">
                  <Details.Item label={_t("year")} value={modelPeriodRange ? (modelPeriodRange.start + " - " + modelPeriodRange.end) : "-"} />
                  <Details.Item label={_t("model")} value={formatValue(modelDesignation)} />

                  <Details.Item
                    label={_t("subModel")}
                    value={formatValue(equipmentVariant)}
                  />

                  <Details.Item
                    label={_t("variant")}
                    value={formatValue(modelPeriod)}
                  />


                  <Details.Item label={_t("gearbox")} value={formatValue(gearbox)} />
                  <Details.Item label={_t("fuel_type")} value={formatValue(fuel)} />
                  <Details.Item label={_t("engine_size")} value={formatValue(engineSize)} />
                  <Details.Item
                    label={_t("number_of_doors")}
                    value={format.number(isVehicle(vehicle) ? vehicle?.remoteData?.dat?.raw?.doorCount : null)}
                  />
                  <Details.Item label={_t("drive_wheel")} value={formatValue(driveType)} />
                  <Details.Item
                    label={_t("dat_net_price")}
                    value={format.price(vehicle.remoteData?.dat?.raw?.originalNetPrice, 0, "EUR")}
                  />
                  <Details.Item
                    label={_t("number_of_seats")}
                    value={format.number(vehicle.remoteData?.dat?.raw?.seatsCount)}
                  />
                  <Details.Item
                    label={_t("number_of_gears")}
                    value={format.number(vehicle.remoteData?.dat?.raw?.gearCount)}
                  />
                  <Details.Item
                    label={_t("drive_wheel")}
                    value={vehicle.remoteData?.dat?.raw?.driveWheel}
                  />
                  <Details.Item
                    label={_t("cylinders")}
                    value={format.number(vehicle.remoteData?.dat?.raw?.cylinders)}
                  />
                  <Details.Item
                    label={_t("battery", "type")}
                    value={formatValue(vehicle.remoteData?.dat?.raw?.batteryType)}
                  />
                </Details>
              </div>
            </Panel>
          </Collapse>
        )}
      </Details>
    </div>
  );
};

export default VehicleModelData;
